/**
 * File Upload Field Component
 *
 * A field that supports toggling between:
 * - Text mode: Manual base64 text entry with expression/variable interpolation support
 * - Upload mode: File picker (browse or drag-and-drop) that auto-encodes to base64
 *
 * When a file is picked in Upload mode, this component auto-populates:
 * - contentBase64 (own value) via onChange
 * - fileName via onParameterChange
 * - mimeType via onParameterChange
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload,
  File,
  X,
  Type,
  HardDrive,
  HelpCircle,
  AlertTriangle,
  Code2,
  Sparkles,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { DroppableTextarea } from '../../node-editor/DroppableTextarea';
import { cn } from '@/lib/utils';

interface InputData {
  nodeId: string;
  nodeName: string;
  output: any;
  sourceHandle?: string;
  nodeDistance: number;
}

interface FileUploadFieldProps {
  property: InferredPropertySchema;
  value: string;
  onChange: (value: string) => void;
  onParameterChange?: (key: string, value: any) => void;
  error?: string;
  allParameters?: Record<string, any>;
  inputData?: InputData[];
}

// Graph API simple upload limit
const GRAPH_SIMPLE_UPLOAD_LIMIT = 4 * 1024 * 1024; // 4MB

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function containsExpression(value: string): boolean {
  return /\{\{.*?\}\}/.test(value || '');
}

// Extract fields from an object recursively for expression browser
function extractFields(obj: any, prefix = ''): { path: string; value: any; type: string }[] {
  const fields: { path: string; value: any; type: string }[] = [];
  if (!obj || typeof obj !== 'object') return fields;

  if (Array.isArray(obj)) {
    if (obj.length > 0 && typeof obj[0] === 'object') {
      fields.push(...extractFields(obj[0], prefix ? `${prefix}[0]` : '[0]'));
    }
    return fields;
  }

  Object.keys(obj).forEach((key) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];
    const type = Array.isArray(value) ? 'array' : value === null ? 'null' : typeof value;
    fields.push({ path, value, type });
    if (typeof value === 'object' && value !== null && path.split('.').length < 5) {
      fields.push(...extractFields(value, path));
    }
  });

  return fields;
}

export function FileUploadField({
  property,
  value,
  onChange,
  onParameterChange,
  error,
  allParameters,
  inputData = [],
}: FileUploadFieldProps) {
  const [mode, setMode] = useState<'text' | 'upload'>(() => {
    // If there's already an expression value, default to text mode
    if (containsExpression(value)) return 'text';
    return 'upload';
  });
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileInfo, setFileInfo] = useState<{
    name: string;
    size: number;
    type: string;
  } | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [fieldBrowserOpen, setFieldBrowserOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // If value is cleared externally, clear fileInfo
  useEffect(() => {
    if (!value && fileInfo) {
      setFileInfo(null);
    }
  }, [value, fileInfo]);

  // Process a selected file: read as base64, populate fields
  const processFile = useCallback((file: globalThis.File) => {
    setIsReading(true);

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Extract base64 portion after "data:...;base64,"
      const base64 = dataUrl.split(',')[1] || '';

      // Set the base64 content
      onChange(base64);

      // Auto-populate fileName and mimeType
      if (onParameterChange) {
        onParameterChange('fileName', file.name);
        onParameterChange('mimeType', file.type || 'application/octet-stream');
      }

      setFileInfo({
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
      });
      setIsReading(false);
    };
    reader.onerror = () => {
      setIsReading(false);
    };
    reader.readAsDataURL(file);
  }, [onChange, onParameterChange]);

  // Handle file input change
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input so same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFile]);

  // Handle drag-and-drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }, [processFile]);

  // Clear uploaded file
  const handleClear = useCallback(() => {
    onChange('');
    setFileInfo(null);
    if (onParameterChange) {
      onParameterChange('mimeType', '');
    }
  }, [onChange, onParameterChange]);

  const showToggle = isHovered || isFocused;

  // Expression-related helpers for text mode
  const availableFields = useMemo(() => {
    const fields: { nodeName: string; path: string; fullPath: string; value: any; type: string }[] = [];
    const sortedInput = [...inputData].sort((a, b) => a.nodeDistance - b.nodeDistance);

    for (const input of sortedInput) {
      let outputData = input.output;
      if (outputData && typeof outputData === 'object' && 'output' in outputData) {
        outputData = outputData.output;
      }
      if (!outputData) continue;

      const nodeFields = extractFields(outputData);
      for (const field of nodeFields) {
        fields.push({
          nodeName: input.nodeName,
          path: field.path,
          fullPath: `$node["${input.nodeName}"].output.${field.path}`,
          value: field.value,
          type: field.type,
        });
      }
    }
    return fields;
  }, [inputData]);

  const fieldsByNode = useMemo(() => {
    const grouped: Record<string, typeof availableFields> = {};
    for (const field of availableFields) {
      if (!grouped[field.nodeName]) grouped[field.nodeName] = [];
      grouped[field.nodeName].push(field);
    }
    return grouped;
  }, [availableFields]);

  const insertExpression = (expression: string) => {
    const newValue = value ? `${value}{{${expression}}}` : `{{${expression}}}`;
    onChange(newValue);
    setFieldBrowserOpen(false);
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'string': return 'bg-blue-500/10 text-blue-600';
      case 'number': return 'bg-green-500/10 text-green-600';
      case 'boolean': return 'bg-purple-500/10 text-purple-600';
      case 'array': return 'bg-pink-500/10 text-pink-600';
      case 'object': return 'bg-orange-500/10 text-orange-600';
      default: return 'bg-gray-500/10 text-gray-600';
    }
  };

  // Render the field browser popover (for text mode)
  const renderFieldBrowser = () => (
    <Popover open={fieldBrowserOpen} onOpenChange={setFieldBrowserOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 text-xs gap-1"
          disabled={Object.keys(fieldsByNode).length === 0}
        >
          <Sparkles className="h-3 w-3" />
          Insert Field
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-2 border-b">
          <p className="text-xs font-medium">Available Fields</p>
          <p className="text-xs text-muted-foreground">Click to insert as expression</p>
        </div>
        <div className="max-h-80 overflow-y-auto p-1">
          {Object.keys(fieldsByNode).length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              <p>No upstream data available</p>
              <p className="text-xs mt-1">Run previous nodes to see available fields</p>
            </div>
          ) : (
            Object.entries(fieldsByNode).map(([nodeName, fields]) => (
              <Collapsible key={nodeName} defaultOpen={Object.keys(fieldsByNode).length === 1}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-1.5 hover:bg-muted rounded text-sm font-medium">
                  <ChevronRight className="h-3 w-3 transition-transform [[data-state=open]>&]:rotate-90" />
                  {nodeName}
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {fields.length}
                  </Badge>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="ml-4 space-y-0.5">
                    {fields.slice(0, 20).map((field, idx) => (
                      <button
                        key={`${field.fullPath}-${idx}`}
                        className="w-full text-left px-2 py-1.5 hover:bg-muted rounded text-xs flex items-center gap-2 group"
                        onClick={() => insertExpression(field.fullPath)}
                      >
                        <code className="font-mono text-xs flex-1 truncate">
                          {field.path}
                        </code>
                        <Badge className={cn('text-[10px] px-1', getTypeBadgeColor(field.type))}>
                          {field.type}
                        </Badge>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <div
      ref={containerRef}
      className="space-y-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
        }
      }}
    >
      {/* Label row with mode toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label htmlFor={property.key}>
            {property.label}
            {property.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {property.helpText && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">{property.helpText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {mode === 'text' && containsExpression(value) && (
            <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-600">
              <Code2 className="h-3 w-3 mr-1" />
              Expression
            </Badge>
          )}
        </div>

        <div
          className={cn(
            'flex items-center gap-1 transition-opacity duration-200',
            showToggle ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          {mode === 'text' && renderFieldBrowser()}

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={mode === 'text' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setMode('text')}
                >
                  <Type className="h-3 w-3 mr-1" />
                  Text
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Enter base64 text or expressions manually</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={mode === 'upload' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setMode('upload')}
                >
                  <Upload className="h-3 w-3 mr-1" />
                  Upload
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Upload a file from your computer</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {property.description && (
        <p className="text-sm text-muted-foreground">{property.description}</p>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Text mode - textarea with expression support */}
      {mode === 'text' && (
        <>
          <DroppableTextarea
            id={property.key}
            value={value || ''}
            onValueChange={onChange}
            placeholder="Paste Base64-encoded content or use {{ }} expressions"
            rows={4}
            className={cn(
              error ? 'border-destructive' : '',
              containsExpression(value) && 'border-blue-500/50 bg-blue-500/5 font-mono text-sm'
            )}
          />
          {containsExpression(value) && (
            <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
              <p className="font-medium mb-1">Expression detected</p>
              <p>The base64 content will be resolved from the upstream node at execution time.</p>
            </div>
          )}
        </>
      )}

      {/* Upload mode */}
      {mode === 'upload' && (
        <>
          {/* File info card (when a file has been selected) */}
          {fileInfo ? (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-start gap-3">
                <div className="rounded-md bg-primary/10 p-2">
                  <File className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{fileInfo.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(fileInfo.size)}
                    </span>
                    <span className="text-xs text-muted-foreground">|</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {fileInfo.type}
                    </span>
                  </div>
                  {fileInfo.size > GRAPH_SIMPLE_UPLOAD_LIMIT && (
                    <div className="flex items-center gap-1 mt-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                      <span className="text-xs text-amber-600">
                        File exceeds 4MB Graph API simple upload limit. Large files may fail.
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  onClick={handleClear}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            /* Drop zone (when no file selected) */
            <div
              className={cn(
                'relative rounded-lg border-2 border-dashed p-6 transition-colors',
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50',
                isReading && 'opacity-50 pointer-events-none'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="rounded-full bg-muted p-3">
                  <Upload className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {isReading ? 'Reading file...' : 'Drop a file here or'}
                  </p>
                  {!isReading && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      browse from your computer
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  File will be encoded as Base64 and MIME type auto-detected
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
